import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Label from "../Label/Label";
import CustomButton from "../CustomButton/CustomButton";
import ModalPopup from "../ModalPopup/ModalPopup";
import {
  createCertificate,
  deleteCertificate,
  fetchCertificateList,
  updateCertificate,
} from "../../services/certificateApi";
import { fetchUsers, getUsersRequestUrl } from "../../services/usersApi";
import {
  assertAdminAuth,
  formatDisplayDate,
  getApiErrorMessage,
} from "../../services/apiClient";
import { isAdminUser, isLoggedIn } from "../../util/auth";
import "./CertificateAdminTab.scss";

const emptyCertificateForm = {
  userId: "",
  startDate: null,
  endDate: null,
};

const normalizeRoles = (roles) =>
  (Array.isArray(roles) ? roles : [roles])
    .filter(Boolean)
    .map((role) => String(role).toUpperCase());

const getInternshipUsers = (items = []) =>
  items
    .filter((user) => {
      const roles = normalizeRoles(user.roles);

      return roles.includes("USER") && !roles.includes("ADMIN");
    })
    .sort((left, right) => {
      const leftName = [left.firstName, left.lastName].filter(Boolean).join(" ");
      const rightName = [right.firstName, right.lastName]
        .filter(Boolean)
        .join(" ");

      return leftName.localeCompare(rightName);
    });

const canManageCertificates = () => isLoggedIn() && isAdminUser();

const toIsoDate = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toISOString().slice(0, 10);
};

const CertificateAdminTab = ({ labels = {} }) => {
  const [certificates, setCertificates] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoadError, setUsersLoadError] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [certificateForm, setCertificateForm] = useState(emptyCertificateForm);
  const [isSaving, setIsSaving] = useState(false);

  const loadCertificates = async () => {
    if (!canManageCertificates()) {
      setIsLoading(false);
      setLoadError("Admin login required to manage certificates.");
      return;
    }

    setIsLoading(true);
    setLoadError("");

    try {
      const items = await fetchCertificateList();
      setCertificates(items);
    } catch (error) {
      console.error("Failed to load certificates", error);
      setCertificates([]);
      setLoadError(
        getApiErrorMessage(
          error,
          "Failed to load certificates. Ensure you are logged in as admin and the API is running.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!canManageCertificates()) {
      setUsersLoadError("Admin login required to load users.");
      return;
    }

    setIsLoadingUsers(true);
    setUsersLoadError("");

    try {
      const items = await fetchUsers();
      const internshipUsers = getInternshipUsers(items);
      setUsers(internshipUsers);

      if (!internshipUsers.length) {
        setUsersLoadError(
          `No internship users returned from ${getUsersRequestUrl()}. Seed John, Jane, and Priya or sign up users via POST /auth/signup.`,
        );
      }
    } catch (error) {
      console.error("Failed to load users", error);
      setUsers([]);
      setUsersLoadError(
        getApiErrorMessage(
          error,
          `Failed to load users from ${getUsersRequestUrl()}. Ensure cursor_API is running and the React dev server was restarted after .env changes.`,
        ),
      );
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadCertificates();
    loadUsers();
  }, []);

  const openCreateModal = () => {
    if (!assertAdminAuth()) {
      return;
    }

    setEditingCertificate(null);
    setCertificateForm(emptyCertificateForm);
    setShowModal(true);
    loadUsers();
  };

  const openEditModal = (certificate) => {
    setEditingCertificate(certificate);
    setCertificateForm({
      userId: certificate.userId || "",
      startDate: certificate.startDate ? new Date(certificate.startDate) : null,
      endDate: certificate.endDate ? new Date(certificate.endDate) : null,
    });
    setShowModal(true);
  };

  const handleSaveCertificate = async () => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!certificateForm.startDate || !certificateForm.endDate) {
      window.alert("Please select start and end dates.");
      return;
    }

    if (!editingCertificate && !certificateForm.userId.trim()) {
      window.alert("Please select a user.");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        startDate: toIsoDate(certificateForm.startDate),
        endDate: toIsoDate(certificateForm.endDate),
      };

      if (editingCertificate?.certificateId) {
        await updateCertificate(editingCertificate.certificateId, payload);
      } else {
        const createdCertificate = await createCertificate({
          userId: certificateForm.userId.trim(),
          ...payload,
        });

        window.alert(
          `Certificate created.\nCertificate ID: ${createdCertificate.certificateId}\nShare this UUID on the verify page along with the holder's date of birth.`,
        );
      }

      setShowModal(false);
      await loadCertificates();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to save certificate."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCertificate = async (certificateId) => {
    if (!assertAdminAuth()) {
      return;
    }

    if (!window.confirm("Delete this certificate?")) {
      return;
    }

    try {
      await deleteCertificate(certificateId);
      await loadCertificates();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to delete certificate."));
    }
  };

  const getUserName = (certificate) => {
    const name = [certificate.firstName, certificate.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return name || certificate.userId || "—";
  };

  const isDelivered = (certificate) => {
    if (!certificate.endDate) {
      return "no";
    }

    const endDate = new Date(certificate.endDate);

    return endDate <= new Date() ? "yes" : "no";
  };

  return (
    <div className="certificate_Admin_Tab">
      <div className="certificate_Admin_Header">
        <Label title={labels.title || "Certificates"} className="heading" />
        <CustomButton
          title={labels.addBtn || "Add Certificate"}
          onClick={openCreateModal}
        />
      </div>

      {loadError && (
        <Label title={loadError} className="certificate_Load_Error" />
      )}

      <div className="certificateContainer">
        <div className="tab">
          <div>SL. NO</div>
          <div>User Name</div>
          <div>Certificate ID (UUID)</div>
          <div>Date of Birth</div>
          <div>Start Date</div>
          <div>End Date</div>
          <div>Delivered</div>
          <div>Action</div>
        </div>

        <div className="tableData">
          {isLoading ? (
            <div className="customers empty_Row">
              <div>Loading certificates...</div>
            </div>
          ) : certificates.length === 0 ? (
            <div className="customers empty_Row">
              <div>No certificates found. Add a certificate to get started.</div>
            </div>
          ) : (
            certificates.map((certificate, index) => (
              <div className="customers" key={certificate.certificateId}>
                <div>{index + 1}.</div>
                <div>{getUserName(certificate)}</div>
                <div className="certificate_Id_Cell">
                  {certificate.certificateId}
                </div>
                <div>{certificate.dateOfBirth || "—"}</div>
                <div>{formatDisplayDate(certificate.startDate)}</div>
                <div>{formatDisplayDate(certificate.endDate)}</div>
                <div>{isDelivered(certificate)}</div>
                <div className="certificate_Actions">
                  <span
                    className="icon-edit cursorP"
                    title={labels.editBtn || "Edit"}
                    onClick={() => openEditModal(certificate)}
                  />
                  <span
                    className="icon-delete cursorP"
                    title={labels.deleteBtn || "Delete"}
                    onClick={() =>
                      handleDeleteCertificate(certificate.certificateId)
                    }
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ModalPopup
        showModal={showModal}
        onHide={() => setShowModal(false)}
        onHideClick={() => setShowModal(false)}
        onButtonClick={handleSaveCertificate}
        headerTitle={
          editingCertificate
            ? labels.editBtn || "Edit Certificate"
            : labels.addBtn || "Add Certificate"
        }
        buttonTitle={isSaving ? "Saving..." : "Save"}
      >
        <Form>
          {!editingCertificate && (
            <Form.Group className="mb-3">
              <Form.Label>User</Form.Label>
              <Form.Select
                value={certificateForm.userId}
                onChange={(event) =>
                  setCertificateForm((current) => ({
                    ...current,
                    userId: event.target.value,
                  }))
                }
                disabled={isLoadingUsers}
              >
                <option value="">
                  {isLoadingUsers ? "Loading users..." : "Select a user"}
                </option>
                {users.map((user) => {
                  const name = [user.firstName, user.lastName]
                    .filter(Boolean)
                    .join(" ")
                    .trim();
                  const label = name
                    ? `${name} (${user.email})`
                    : user.email || user.userId;

                  return (
                    <option key={user.userId} value={user.userId}>
                      {label}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Text className="text-muted">
                Select the user who completed the internship.
              </Form.Text>
              {usersLoadError && (
                <Form.Text className="text-danger d-block mt-2">
                  {usersLoadError}
                </Form.Text>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <DatePicker
              selected={certificateForm.startDate}
              onChange={(date) =>
                setCertificateForm((current) => ({
                  ...current,
                  startDate: date,
                }))
              }
              className="form-control"
              placeholderText="Select start date"
              dateFormat="dd-MM-yyyy"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <DatePicker
              selected={certificateForm.endDate}
              onChange={(date) =>
                setCertificateForm((current) => ({
                  ...current,
                  endDate: date,
                }))
              }
              className="form-control"
              placeholderText="Select end date"
              dateFormat="dd-MM-yyyy"
              minDate={certificateForm.startDate || undefined}
            />
          </Form.Group>

          {editingCertificate?.certificateId && (
            <Label
              title={`Certificate ID: ${editingCertificate.certificateId}${editingCertificate.dateOfBirth ? ` | Holder DOB: ${editingCertificate.dateOfBirth}` : ""}`}
              className="certificate_Id_Label"
            />
          )}
        </Form>
      </ModalPopup>
    </div>
  );
};

export default CertificateAdminTab;
